import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classnames from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import { FloatDatePicker } from 'ui-components/components/float-date-picker';
import { noop } from 'ui-components/default-props/noop';
import Global from '../../../store/global-selectors';
import { useDdpCountryConfig } from '../../../hooks/use-ddp-country-config';
import { SurchargeTooltip } from '../../../financial-services-ui/surcharge/surcharge-tooltip';
import { Tooltip } from 'ui-components/components/tooltip';

import FormattedCurrencyContainer from '../../../components/formatted-currency/container';
import { DateService } from '../../../services/date-service';
import style from './cost-review-line-item-summary.module.css';
import { messages } from './messages';

/**
 * @param {object} props
 * @param {import('../../../hooks/cost-review/use-combined-cost-info').CombinedCostInfo} props.combinedCostInfo
 * @param {number} props.creditCardProcessingFee
 * @param {number} props.creditCardProcessingFeePercentage
 * @param {string} props.shipDate
 * @param {(newShipDate: string) => void} props.onShipDateChanged
 * @param {string} props.userDateFormat
 * @param {boolean} props.readOnly
 * @param {string[]} props.fulfillmentPlanIds
 * @returns {React.Element}
 */
const CostReviewLineItemSummary = ({
  combinedCostInfo,
  creditCardProcessingFee = 0.0,
  creditCardProcessingFeePercentage = 0.0,
  shipDate,
  onShipDateChanged = noop,
  userDateFormat = 'MM/DD/YYYY',
  readOnly = false,
  fulfillmentPlanIds = [],
}) => {
  const {
    totalPurchaseAmount = 0,
    nonWalletTotalPostageCost = 0,
    nonWalletPurchaseAmount = 0,
    totalWalletPostageCost = 0,
    totalWalletPurchaseAmount = 0,
  } = combinedCostInfo;
  const total = totalPurchaseAmount + creditCardProcessingFee;

  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const { isDdpAvailable: isPrepaidDutiesAndTaxEnabled } = useDdpCountryConfig();
  const firstFulfillmentPlanId = fulfillmentPlanIds[0];

  const currentSellerLabelProviderId = useSelector((state) =>
    firstFulfillmentPlanId
      ? Global.selectEntitiesFulfillmentPlanProperty(state, {
          fulfillmentPlanId: firstFulfillmentPlanId,
          property: 'labelConfiguration.sellerLabelProviderId',
        })
      : null,
  );

  const shippingConfiguration = useSelector((state) =>
    firstFulfillmentPlanId ? Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId: firstFulfillmentPlanId }) : {},
  );

  const sellerServices = useSelector(Global.selectSellerServices);

  const shouldShowDutiesAndTaxesTooltip = useMemo(() => {
    if (!currentSellerLabelProviderId || !sellerServices) return false;

    const isBillToSenderAvailable = sellerServices.some(
      (service) => service.sellerLabelProviderId === currentSellerLabelProviderId && service.optionCodes?.includes('bill-to-sender'),
    );

    if (!isBillToSenderAvailable) return false;

    const isDeliveredDutyPaidEnabled = Boolean(shippingConfiguration?.options?.deliveredDutyPaid);
    return !isDeliveredDutyPaidEnabled;
  }, [currentSellerLabelProviderId, sellerServices, shippingConfiguration]);
  return (
    <div
      className={classnames(style['cost-review-summary'], {
        [style['read-only']]: readOnly,
      })}
    >
      <div
        className={classnames(style['ship-date'], {
          [style['hide-ship-date']]: readOnly,
        })}
      >
        <FormattedMessage {...messages.scheduledShipDate} />
        <div>
          <FloatDatePicker
            additionalDateFormats={DateService.getAdditionalDateFormats()}
            allowPastDates={false}
            className={style['ship-date-input']}
            dateFormat={userDateFormat}
            label={<FormattedMessage {...messages.shipDate} />}
            onChange={onShipDateChanged}
            sellerTimeZone={sellerTimeZone}
            value={shipDate}
          />
        </div>
      </div>
      <div className={style['cost-summary-section']}>
        {totalWalletPostageCost > 0 && (
          <>
            <div className={style['line-item']}>
              <div>
                <FormattedMessage {...messages.shipstationServicesPostage} />
              </div>
              <div>
                <FormattedCurrencyContainer value={totalWalletPostageCost} />
              </div>
            </div>
            {totalWalletPurchaseAmount > 0 && (
              <div className={style['line-item']}>
                <div>
                  <FormattedMessage {...messages.fundsToAdd} />
                </div>
                <div>
                  <FormattedCurrencyContainer value={totalWalletPurchaseAmount} />
                </div>
              </div>
            )}
          </>
        )}
        {creditCardProcessingFee > 0 && (
          <div className={style['line-item']}>
            <div className={style['line-item-with-icon']}>
              <FormattedMessage
                {...messages.surcharge}
                values={{
                  creditCardProcessingFeePercentage: creditCardProcessingFeePercentage.toFixed(1),
                }}
              />
              <SurchargeTooltip creditCardProcessingFeePercentage={creditCardProcessingFeePercentage} />
            </div>
            <div>
              <FormattedCurrencyContainer value={creditCardProcessingFee} />
            </div>
          </div>
        )}
        {nonWalletTotalPostageCost > 0 && (
          <>
            <div className={style['line-item']}>
              <div>
                <FormattedMessage {...messages.carrierPostageCost} />
              </div>
              <div>
                <FormattedCurrencyContainer value={nonWalletTotalPostageCost} />
              </div>
            </div>
            {nonWalletPurchaseAmount > 0 && (
              <div className={style['line-item']}>
                <div>
                  <FormattedMessage {...messages.carrierFunds} />
                </div>
                <div>
                  <FormattedCurrencyContainer value={nonWalletPurchaseAmount} />
                </div>
              </div>
            )}
          </>
        )}
        <div className={classnames(style['line-item'], style.total)}>
          <div>
            <FormattedMessage {...messages.totalCharge} />
            {isPrepaidDutiesAndTaxEnabled && shouldShowDutiesAndTaxesTooltip && (
              <Tooltip position='top' text={<FormattedMessage {...messages.dutiesAndTaxesTooltip} />}>
                <span className={style['tooltip-wrapper']}>
                  <FontAwesomeIcon className={style['tooltip-icon']} icon={faInfoCircle} />
                </span>
              </Tooltip>
            )}
          </div>
          <div>
            <FormattedCurrencyContainer value={total} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostReviewLineItemSummary;
